import { DateTime } from 'luxon'

// The question types that are answered and printed differently
export enum QUESTION_TYPE {
  APP_CUSTOM = 'app-custom',
  CHECKBOX = 'checkbox',
  DATE = 'date',
  MULTISELECT = 'multiselect',
  MULTISELECT_EDIT = 'multiselect-edit',
  MULTISELECT_EDIT_QUILL = 'multiselect-edit-quill',
  NUMBER = 'number',
  PDF_UPLOAD = 'pdf-upload',
  RADIO = 'radio',
  RANGE = 'range',
  SELECT = 'select',
  SELECT_EDIT = 'select-edit',
  SELECT_EDIT_QUILL = 'select-edit-quill',
  TEXT = 'text',
  TEXTAREA = 'textarea',
  TEXTAREA_QUILL = 'textarea-quill'
}

const SURVEY_ANSWERS_API_URL = 'css/survey-answers'
// Sent with the request and its response after every saved, created or deleted survey answer
export const SURVEY_ANSWER_SAVED_EVENT = 'SURVEY_ANSWER_SAVED_EVENT'

export function isSurveyAnswerWrite(url: string, method: string): boolean {
  return method !== 'GET' && url.startsWith(SURVEY_ANSWERS_API_URL)
}

// How an upload answer settles its checkpoint, besides plainly yes or no
export enum INPUT_TYPE {
  COMPLAINT = 'complaint',
  NOT_REQUIRED = 'notRequired',
  SUMMARY_DEVIATION = 'summaryDeviation'
}

// Option-or-free-text types; the -quill variants keep their text as Quill HTML
export const SELECT_EDIT_QUESTION_TYPES = new Set<string>([
  QUESTION_TYPE.SELECT_EDIT,
  QUESTION_TYPE.MULTISELECT_EDIT,
  QUESTION_TYPE.SELECT_EDIT_QUILL,
  QUESTION_TYPE.MULTISELECT_EDIT_QUILL
])
export const SINGLE_SELECT_EDIT_QUESTION_TYPES = new Set<string>([
  QUESTION_TYPE.SELECT_EDIT,
  QUESTION_TYPE.SELECT_EDIT_QUILL
])
export const QUILL_SELECT_EDIT_QUESTION_TYPES = new Set<string>([
  QUESTION_TYPE.SELECT_EDIT_QUILL,
  QUESTION_TYPE.MULTISELECT_EDIT_QUILL
])

// Reads a marker of the form name(1,2,3)
function parseIdListMarker(name: string, taskDescription?: string | null): number[] | null {
  const match = taskDescription?.match(new RegExp(`^${name}\\(([\\d,\\s]+)\\)$`))
  if (!match) return null
  const ids = match[1]
    .split(',')
    .map((id) => parseInt(id.trim()))
    .filter((id) => !isNaN(id))
  return ids.length ? ids : null
}

export function parseSumQuestionIds(taskDescription?: string | null): number[] | null {
  return parseIdListMarker('sum', taskDescription)
}

// task_description can hold more than one marker at once, separated by ";" (e.g. a question
// that's itself both a hideQuestionWhenInputTypeTrue trigger and a complaintSummary source)
// — matched as one segment among others rather than requiring it to be the whole string.
// The marker can name several questions, e.g. hideQuestionWhenInputTypeTrue:229,231.
export function parseHideQuestionWhenInputTypeTrueIds(taskDescription?: string | null): number[] {
  const match = taskDescription?.match(
    /(?:^|;)hideQuestionWhenInputTypeTrue(?:OrNotRequired)?:(\d+(?:,\d+)*)(?:;|$)/
  )
  return match ? match[1].split(',').map(Number) : []
}

// The hideQuestionWhenInputTypeTrueOrNotRequired form also counts "Nicht erforderlich"
export function isHideQuestionTriggerMet(
  taskDescription: string | null | undefined,
  inputType: unknown
): boolean {
  if (inputType === true) return true
  return (
    inputType === INPUT_TYPE.NOT_REQUIRED &&
    /(?:^|;)hideQuestionWhenInputTypeTrueOrNotRequired:\d+(?:,\d+)*(?:;|$)/.test(
      taskDescription || ''
    )
  )
}

// A question is struck through once the siblings naming it in hideQuestionWhenInputTypeTrue
// are ticked. Sub-questions exclude each other, so one ticked sibling is enough; a top-level
// conclusion (e.g. "Abweichungen festgestellt") only becomes moot once every trigger is ticked.
export function isDisabledBySibling(question: SurveyQuestion, siblings: SurveyQuestion[]): boolean {
  const triggers = siblings.filter((sibling) =>
    parseHideQuestionWhenInputTypeTrueIds(sibling.task_description).includes(question.id)
  )
  const isTriggerMet = (trigger: SurveyQuestion) =>
    isHideQuestionTriggerMet(trigger.task_description, getLatestInputType(trigger))
  if (!triggers.length) return false
  return question.parent_question_id ? triggers.some(isTriggerMet) : triggers.every(isTriggerMet)
}

function getLatestInputType(question: SurveyQuestion): unknown {
  try {
    return JSON.parse(question.answers?.[question.answers.length - 1]?.value || '{}').inputType
  } catch {
    return null
  }
}

// "Die Errichtung der Anlage erfolgte durch Firma", which can pick a contact
export function hasInstalledByCompanyMarker(taskDescription?: string | null): boolean {
  return /(?:^|;)Kontaktauswahl(?:;|$)/.test(taskDescription || '')
}

// skipCertificateSeedForReportTypes:2 — report types that do not take the installing company
// from the first company certificate
export function parseSkipCertificateSeedReportTypeIds(
  taskDescription?: string | null
): Set<number> {
  const match = taskDescription?.match(/(?:^|;)skipCertificateSeedForReportTypes:([\d,\s]+)(?:;|$)/)
  return new Set(
    match
      ? match[1]
          .split(',')
          .map((id) => parseInt(id.trim()))
          .filter((id) => !isNaN(id))
      : []
  )
}

// complaintSummary may come without target ids (no "Aktuelle Mängel" source yet)
export function hasComplaintSummaryMarker(taskDescription?: string | null): boolean {
  return /(?:^|;)complaintSummary(?::\d+(?:,\d+)*)?(?:;|$)/.test(taskDescription || '')
}

// The questions whose deficient children make up "Aktuelle Mängel", e.g. complaintSummary:119,125
export function parseComplaintSummaryQuestionIds(taskDescription?: string | null): number[] {
  const match = taskDescription?.match(/(?:^|;)complaintSummary:(\d+(?:,\d+)*)(?:;|$)/)
  return match ? match[1].split(',').map(Number) : []
}

export function parseParentOptionIds(taskDescription?: string | null): number[] | null {
  return parseIdListMarker('parentOptionIds', taskDescription)
}

// The question's own children show once it is answered with anything but these options
export function parseShowChildrenUnlessOptionIds(taskDescription?: string | null): number[] | null {
  return parseIdListMarker('showChildrenUnlessOptionIds', taskDescription)
}

// Searches catalog components of a category and fills in a field, e.g. componentSearch:detector:type
export function parseComponentSearch(
  taskDescription?: string | null
): { category: string; field: string } | null {
  const match = taskDescription?.match(/(?:^|;)componentSearch:([a-z_]+):([a-zA-Z]+)(?:;|$)/)
  return match ? { category: match[1], field: match[2] } : null
}

// Filled from the component picked in a sibling's search, e.g. componentField:testReportNumber
export function parseComponentField(taskDescription?: string | null): string | null {
  return taskDescription?.match(/(?:^|;)componentField:([a-zA-Z]+)(?:;|$)/)?.[1] || null
}

export function parseDefaultOptionId(taskDescription?: string | null): number | null {
  const match = taskDescription?.match(/^defaultOption:(\d+)$/)
  if (!match) return null
  const id = parseInt(match[1])
  return isNaN(id) ? null : id
}

// The question's options are the project's main type and its sub types
export function hasProjectTypeOptionsMarker(taskDescription?: string | null): boolean {
  return /(?:^|;)optionsFromProjectTypes(?:;|$)/.test(taskDescription || '')
}

// "Inspektion vor Ort", the date the next revision is counted from
export function hasInspectionOnSiteMarker(taskDescription?: string | null): boolean {
  return /(?:^|;)inspectionOnSite(?:;|$)/.test(taskDescription || '')
}

// Unanswered, the date question shows the on-site inspection date plus the revision cycle
export function hasNextRevisionDefaultMarker(taskDescription?: string | null): boolean {
  return /(?:^|;)defaultNextRevision(?:;|$)/.test(taskDescription || '')
}

const DEFAULT_REVISION_CYCLE_MONTHS = 24
const DATE_ANSWER_FORMAT = 'dd.MM.yyyy'
const MONTH_YEAR_ANSWER_FORMAT = 'MM/yyyy'

// The answered on-site inspection date; a date question stores dd.MM.yyyy or MM/yyyy
export function getInspectionOnSiteDate(
  surveyQuestions: SurveyQuestion[]
): { date: DateTime; isMonthYear: boolean } | null {
  const question = surveyQuestions.find((q) => hasInspectionOnSiteMarker(q.task_description))
  const value = question?.answers?.[question.answers.length - 1]?.value || ''
  const fullDate = DateTime.fromFormat(value, DATE_ANSWER_FORMAT)
  if (fullDate.isValid) return { date: fullDate, isMonthYear: false }
  const monthYear = DateTime.fromFormat(value, MONTH_YEAR_ANSWER_FORMAT)
  return monthYear.isValid ? { date: monthYear, isMonthYear: true } : null
}

// "Nächste Revision" until answered: the on-site inspection date plus the project's revision cycle
export function getDefaultNextRevisionAnswer(
  surveyQuestions: SurveyQuestion[],
  project?: Project | null
): string {
  const inspection = getInspectionOnSiteDate(surveyQuestions)
  if (!inspection) return ''
  return inspection.date
    .plus({ months: project?.revision_cycle || DEFAULT_REVISION_CYCLE_MONTHS })
    .toFormat(inspection.isMonthYear ? MONTH_YEAR_ANSWER_FORMAT : DATE_ANSWER_FORMAT)
}

export const CHECKBOX_CHECKED_VALUE = '1'

export const NOT_REQUIRED_ANSWER_VALUE = JSON.stringify({ inputType: INPUT_TYPE.NOT_REQUIRED })

export function isNotRequiredMarker(value?: string | null): boolean {
  if (!value) return false
  try {
    return JSON.parse(value)?.inputType === INPUT_TYPE.NOT_REQUIRED
  } catch {
    return false
  }
}
